/**
 * The map step of the RERA import. The server outlines each plot and says what lies beyond
 * each of its four sides, by the angle that side faces on the picture. Which of those sides is
 * "north" depends on how the plan is turned, which the reviewer sets, so the compass sides,
 * the facing and the items per side are worked out here and follow the north control live.
 */

export const SIDES = ['north', 'east', 'south', 'west'];

export const FACING_OPTIONS = [
  { value: 'north', label: 'North' },
  { value: 'north_east', label: 'North-East' },
  { value: 'east', label: 'East' },
  { value: 'south_east', label: 'South-East' },
  { value: 'south', label: 'South' },
  { value: 'south_west', label: 'South-West' },
  { value: 'west', label: 'West' },
  { value: 'north_west', label: 'North-West' }
];

export const FACING_LABELS = Object.fromEntries(FACING_OPTIONS.map((o) => [o.value, o.label]));

/** Compass bearing of each facing, for drawing an arrow. */
export const FACING_BEARING = {
  north: 0,
  north_east: 45,
  east: 90,
  south_east: 135,
  south: 180,
  south_west: 225,
  west: 270,
  north_west: 315
};

const DIAGONAL = {
  'north,east': 'north_east',
  'east,south': 'south_east',
  'south,west': 'south_west',
  'north,west': 'north_west'
};

const ROAD = 'Road';
/** Things that say nothing useful about a side, so they are not written as items. */
const NOT_ITEMS = new Set(['Open land', 'Outside the plan', 'Green area']);

/** The compass side a bearing (degrees clockwise from north) falls in. */
export const sideOfBearing = (bearing) => SIDES[Math.floor(((((bearing + 45) % 360) + 360) % 360) / 90) % 4];

/**
 * edges: [{ angle, what }], the angle each side faces on the picture (0 = up, clockwise).
 * northAngle: how far the plan's north arrow is turned clockwise from up.
 * Returns the thing beyond each compass side, the facing, and how sure that is:
 *   clear   one road, so the plot faces it
 *   corner  roads on two neighbouring sides: a diagonal facing, to be checked
 *   choose  roads on opposite sides (or three): the map cannot say which is the front
 *   none    no road touches it
 */
export function directionsFromEdges(edges, northAngle = 0) {
  const sides = {};
  (edges || []).forEach((e) => {
    const side = sideOfBearing(e.angle - northAngle);
    if (!sides[side]) sides[side] = e.what;
  });
  const roads = SIDES.filter((s) => sides[s] === ROAD);
  let facing = null;
  let confidence = 'none';
  let why = 'No road touches this plot.';
  if (roads.length === 1) {
    [facing] = roads;
    confidence = 'clear';
    why = `Road on the ${roads[0]} side only.`;
  } else if (roads.length === 2 && DIAGONAL[roads.join(',')]) {
    facing = DIAGONAL[roads.join(',')];
    confidence = 'corner';
    why = `Corner plot: roads on the ${roads[0]} and ${roads[1]} sides, so it faces both. Please check.`;
  } else if (roads.length >= 2) {
    confidence = 'choose';
    why = `Roads on the ${roads.join(', ')} sides. The map cannot say which is the front: choose one.`;
  }
  const items = Object.fromEntries(SIDES.map((s) => [s, sides[s] && !NOT_ITEMS.has(sides[s]) ? [sides[s]] : []]));
  return { sides, roads, facing, confidence, why, items };
}

/** Directions as they will be saved: what was predicted, with anything the reviewer typed on top. */
export function finalDirection(derived, edit = {}) {
  const out = { facing: edit.facing !== undefined ? edit.facing : derived.facing };
  SIDES.forEach((s) => {
    out[s] = edit[s] !== undefined ? edit[s] : derived.items[s];
  });
  return out;
}

export const setNorthAngle = (draft, angle) => ({
  ...draft,
  layout: draft.layout ? { ...draft.layout, northAngle: angle } : draft.layout
});

/** Remember a change the reviewer made to one unit's directions; north changes will not undo it. */
export const setDirectionEdit = (draft, plotKey, patch) => ({
  ...draft,
  directionEdits: { ...(draft.directionEdits || {}), [plotKey]: { ...(draft.directionEdits?.[plotKey] || {}), ...patch } }
});

/** Names split from a typed cell: "Road, E9" -> ["Road", "E9"]. */
export const splitNames = (text) =>
  Array.from(new Set(String(text || '').split(',').map((s) => s.trim()).filter(Boolean)));

/** Plot keys of the units still in the draft (the reviewer may have removed some). */
export const unitKeys = (draft) =>
  new Set(
    draft.blocks.flatMap((b) => b.levels.flatMap((l) => l.units.map((u) => (u.plotKey ?? u.unit_name).toLowerCase())))
  );

/**
 * What is sent to the server: the draft without the screen-only edits, the layout limited to
 * units that still exist, and the directions worked out from the sides and north, with the
 * reviewer's changes on top. A draft with no map is sent unchanged.
 */
export function buildCommitDraft(draft) {
  const { directionEdits, ...rest } = draft;
  if (!draft.layout) return rest;
  const keep = unitKeys(draft);
  const plots = draft.layout.plots.filter((p) => keep.has(p.plotKey.toLowerCase()));
  const directions = {};
  plots.forEach((p) => {
    directions[p.plotKey] = finalDirection(directionsFromEdges(p.edges, draft.layout.northAngle), directionEdits?.[p.plotKey]);
  });
  return { ...rest, layout: { ...draft.layout, plots }, directions };
}
